/**
 * 言語別の生成状況画面 (SBI-17)。
 *
 * 「特定言語だけ音声が生成されない」というサイレント欠落を、管理画面で確認する。
 * ControlPanel API の `GET /language-health` (ContentsTable の `contents_schedule[]` を
 * 言語別に集計) を利用する。
 *
 * 画面構成:
 *   - 言語フィルタ (すべて / 日英中韓): 選んだ言語だけを表示する。
 *   - 言語ごとの表: 放送数 (entry) / 音声欠落 / 文面欠落。**音声欠落が 1 件でもある言語は
 *     行を強調**し、「どの言語が落ちているか」が一目で分かるようにする。
 *
 * ⚠️ フィルタ値は Polly の `LanguageCode` (`ja-JP`) ではなく **language_key** (`ja` / `en`)。
 * ⚠️ 走査上限で打ち切られた (`truncated`) 場合は警告を出す。件数が少なく見えても
 *    「欠落が減った」と解釈してはならないため。
 */
import { Spinner } from './Spinner';
import { StatusBanner } from './StatusBanner';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';

import { LanguageHealthClient, type LanguageHealthResponse } from '../api/languageHealth';
import { useAuth } from '../auth/AuthProvider';
import type { AppConfig } from '../config';
import { BASE_LANGUAGE_KEYS } from '../data/pollyVoices';
import { describeApiErrorMessage } from '../utils/apiErrorMessage';
import { languageDisplayName, lossyRows, sortLanguageRows } from '../utils/languageHealth';

export interface LanguageHealthScreenProps {
  config: AppConfig;
}

/** 「すべての言語」を表すセレクトの値 (API には送らない) */
const ALL_LANGUAGES = '';

/** API クライアントの例外を画面表示用の日本語メッセージに整形する (Issue #815) */
function formatApiError(err: unknown): string {
  return describeApiErrorMessage(err);
}

export function LanguageHealthScreen(props: LanguageHealthScreenProps): React.JSX.Element {
  const { config } = props;
  const { getIdToken } = useAuth();

  const apiClient = useMemo(
    () => new LanguageHealthClient({ getIdToken, config }),
    [getIdToken, config],
  );

  const [language, setLanguage] = useState<string>(ALL_LANGUAGES);
  const [health, setHealth] = useState<LanguageHealthResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  /** 取得の世代カウンタ (古いレスポンスで新しい結果を上書きしない防御) */
  const requestIdRef = useRef(0);

  const reload = useCallback(async (): Promise<void> => {
    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    setErrorMessage(null);
    try {
      const res = await apiClient.get(language === ALL_LANGUAGES ? undefined : language);
      if (currentRequestId !== requestIdRef.current) return;
      setHealth(res);
    } catch (err) {
      if (currentRequestId !== requestIdRef.current) return;
      setErrorMessage(formatApiError(err));
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [apiClient, language]);

  useEffect(() => {
    void reload();
    return () => {
      // アンマウント時に世代を進め、進行中リクエストの結果を破棄する。
      // eslint-disable-next-line react-hooks/exhaustive-deps -- ref は DOM ではなくリクエスト世代カウンタ
      ++requestIdRef.current;
    };
  }, [reload]);

  const rows = useMemo(() => (health ? sortLanguageRows(health.by_language) : []), [health]);
  const lossy = useMemo(() => lossyRows(rows), [rows]);

  return (
    <section className="section section--page list-page" data-testid="language-health-section">
      <h2>
        言語別の生成状況
        {loading ? (
          <>
            <Spinner />
            (読み込み中...)
          </>
        ) : (
          ''
        )}
      </h2>

      <p style={{ margin: '0 0 12px', fontSize: 13, color: '#555' }}>
        放送文面の音声が言語ごとに生成されているかを確認します。
        <strong>音声欠落</strong>が 1 件でもある言語は、その言語の放送が一部抜けています (Polly
        が入力を拒否した コンテンツなど)。
      </p>

      <div style={{ margin: '0 0 12px', display: 'flex', gap: 8, alignItems: 'center' }}>
        <label htmlFor="language-health-filter">言語</label>
        <select
          id="language-health-filter"
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          disabled={loading}
          data-testid="language-health-filter"
        >
          <option value={ALL_LANGUAGES}>すべて</option>
          {BASE_LANGUAGE_KEYS.map((key) => (
            <option key={key} value={key}>
              {languageDisplayName(key)}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => void reload()}
          disabled={loading}
          data-testid="language-health-reload"
        >
          再読み込み
        </button>
      </div>

      {errorMessage ? (
        <div role="alert" className="error-message" data-testid="language-health-error">
          {errorMessage}
        </div>
      ) : null}

      {health?.truncated ? (
        <StatusBanner variant="warning" data-testid="language-health-truncated">
          走査上限に達したため、一部のデータのみを集計しています。件数が少なく見えても
          「欠落が減った」とは判断できません。
        </StatusBanner>
      ) : null}

      {health && !loading && lossy.length > 0 ? (
        <StatusBanner variant="error" data-testid="language-health-loss-banner">
          音声が生成されていない言語があります:{' '}
          {lossy.map(([key]) => languageDisplayName(key)).join('、')}
        </StatusBanner>
      ) : null}

      <div className="list-page__body" data-testid="language-health-body">
        <div className="table-wrapper">
          <table className="data-table" data-testid="language-health-table">
            <thead>
              <tr>
                <th>言語</th>
                <th>放送数</th>
                <th>放送のある地点数</th>
                <th>音声欠落</th>
                <th>文面欠落</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(([key, stats]) => {
                const isLossy = stats.missing_audio_key_count > 0;
                return (
                  <tr
                    key={key}
                    data-testid={`language-health-row-${key}`}
                    data-lossy={isLossy ? 'true' : 'false'}
                    style={isLossy ? { background: '#fdecea', fontWeight: 700 } : undefined}
                  >
                    <td>{languageDisplayName(key)}</td>
                    <td>{stats.entry_count}</td>
                    <td>{stats.item_count}</td>
                    <td data-testid={`language-health-missing-audio-${key}`}>
                      {stats.missing_audio_key_count}
                    </td>
                    <td data-testid={`language-health-empty-message-${key}`}>
                      {stats.empty_message_count}
                    </td>
                  </tr>
                );
              })}
              {health && rows.length === 0 && !loading ? (
                <tr>
                  <td colSpan={5} data-testid="language-health-empty">
                    {language === ALL_LANGUAGES
                      ? '放送文面がまだ生成されていません'
                      : `${languageDisplayName(language)} の放送文面はありません`}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        {health ? (
          <p style={{ margin: '8px 0 0', fontSize: 12, color: '#555' }}>
            集計対象: {health.scanned_item_count} 件 / 取得時刻: {health.generated_at}
          </p>
        ) : null}
      </div>
    </section>
  );
}
